import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  CONTRACT_CHANGED_MESSAGE,
  ContractSignError,
  getContract,
  signContract,
} from './api/contract';
import { usePublicTokenResource } from '../usePublicTokenResource';
import { W2_CONTRACT_TID } from './constants/testids';
import { todayISO } from './format';
import { ContractShell } from './components/ContractShell';
import { ContractPending } from './components/ContractPending';
import { ContractSigned } from './components/ContractSigned';
import { ContractLinkError } from './components/ContractLinkError';

/**
 * A fresh local sign (the only source of the signer's name — the wire never
 * returns one; §4 Signed resolve is {state, companyName, signedAt}).
 */
interface LocalSign {
  signerName: string;
  /**
   * Server-recorded signedAt from the sign response (§4). Left undefined when
   * the wire omits it — the confirmation falls back to the re-resolved value
   * rather than a client-clock date (finding L1).
   */
  signedAt?: string;
}

/**
 * Public contract-signing page (ticket 06.5): `/contract/<token>` — the same
 * hosted-page pattern as the W2 employee `/e/<token>` flow (standalone, outside
 * every app layout; the emailed link usually lands on a phone). The resolve prelude
 * (token extraction, loading, invalid-vs-outage, retry) is the shared
 * `usePublicTokenResource`; past it, the page renders exactly one of:
 *
 *   Pending → the pre-populated contract + attestation-gated typed-name signing,
 *             or a cannot-sign notice when the document cannot be rendered faithfully;
 *   Signed  → confirmation only (the link is consumed — no terms, no download);
 *   Expired → friendly "ask for a fresh link";
 *   invalid → 404s land here (no invalid-vs-expired guessing beyond the API).
 */
export const W2ContractPage: React.FC = () => {
  const { contractToken } = useParams<'contractToken'>();
  const [searchParams] = useSearchParams();
  // Path param is canonical (frozen C-3 / D1); the legacy `?t=` read survives
  // only for the bare `/contract` route (missing-token → invalid, and
  // pre-migration links).
  const token = contractToken ?? searchParams.get('t') ?? '';
  const contract = usePublicTokenResource(getContract, token);

  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [localSign, setLocalSign] = useState<LocalSign | null>(null);

  const handleSign = async ({
    signerName,
    federalTaxId,
  }: {
    signerName: string;
    federalTaxId: string;
  }) => {
    setSigning(true);
    setSignError(null);
    try {
      const { signedAt } = await signContract(contract.token, {
        signerName,
        signature: signerName,
        date: todayISO(),
        authorized: true,
        federalTaxId,
        // The digest of the document that was on screen: the server refuses the signature
        // if the contract changed while this page was open.
        renderDigest: contract.data?.renderDigest ?? '',
      });
      // Server-derived single source of truth: show the signedAt the backend
      // recorded (§4 sign response). If the wire omits it, leave it undefined
      // and let the confirmation fall back to the re-resolved value rather than
      // synthesize a client-clock date (finding L1).
      setLocalSign({ signerName, signedAt });
    } catch (err) {
      if (err instanceof ContractSignError && err.resolveAgain) {
        // The contract moved under us (signed elsewhere / expired / token rotated —
        // §4's 409s and 404) — re-resolve so the page lands on the truthful state
        // instead of an inline error. A changed document re-resolves back to Pending,
        // so that one case also has to say why the signature did not go through.
        if (err.dataChanged) {
          setSignError(CONTRACT_CHANGED_MESSAGE);
        }
        await contract.resolveAgain();
      } else {
        // Genuine sign failure (inline error, not a state-move re-resolve). Log
        // status-only for observability (never the payload/token) — finding M2.
        console.error('[w2] contract sign failed', {
          status: err instanceof ContractSignError ? err.status : undefined,
        });
        setSignError(
          err instanceof ContractSignError
            ? err.message
            : 'We couldn’t record your signature. Please try again.'
        );
      }
    } finally {
      setSigning(false);
    }
  };

  switch (contract.phase) {
    case 'loading':
      return (
        <ContractShell>
          <p
            data-testid={W2_CONTRACT_TID.loading}
            className="text-[15px] text-[#5a5a70]"
          >
            Loading your contract…
          </p>
        </ContractShell>
      );

    case 'invalid':
      return (
        <ContractShell>
          <ContractLinkError kind="invalid" />
        </ContractShell>
      );

    case 'outage':
      // Transport/server outage — distinct from a bad link; offer a retry.
      return (
        <ContractShell>
          <div
            data-testid={W2_CONTRACT_TID.loadError}
            className="flex flex-col gap-4 py-2"
          >
            <h1 className="text-3xl font-extrabold text-[#1a1a2e] max-sm:text-2xl">
              We couldn’t load your contract
            </h1>
            <p className="text-[15px] leading-6 text-[#5a5a70]">
              Something went wrong on our side. Please try again in a moment.
            </p>
            <button
              type="button"
              data-testid={W2_CONTRACT_TID.retry}
              onClick={contract.retry}
              className="self-start rounded-full bg-primary px-6 py-3 text-[15px] font-semibold text-white hover:opacity-90"
            >
              Try again
            </button>
          </div>
        </ContractShell>
      );
  }

  // Resolved: render by contract state, with a fresh local sign overriding
  // Pending (the resolve data stays as fetched; the wire is not re-read).
  const data = contract.data;
  if (!data) return null;
  const state = localSign ? 'Signed' : data.state;

  switch (state) {
    case 'Signed':
      return (
        <ContractShell>
          <ContractSigned
            companyName={data.companyName}
            signerName={localSign?.signerName}
            signedAt={localSign?.signedAt ?? data.signedAt}
          />
        </ContractShell>
      );

    case 'Expired':
      return (
        <ContractShell>
          <ContractLinkError kind="expired" />
        </ContractShell>
      );

    default:
      // Fail closed: a document this build cannot render faithfully must not be signable —
      // the signature covers the SERVER's digest of the whole document, so a partial render
      // would sign terms that were never displayed.
      if (data.cannotRender || !data.document) {
        return (
          <ContractShell>
            <ContractLinkError kind="unsupported" />
          </ContractShell>
        );
      }
      return (
        <ContractShell>
          <ContractPending
            data={{ ...data, document: data.document }}
            signing={signing}
            signError={signError}
            onSign={handleSign}
          />
        </ContractShell>
      );
  }
};

export default W2ContractPage;
